import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, X, Award, Star, Crown, Package, Mail } from 'lucide-react';
import { fetchProgram } from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const ProgramPricingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedFormula, setSelectedFormula] = useState('silver');
  const [selectedKit, setSelectedKit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pricingView, setPricingView] = useState('formules');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [program, setProgram] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    fetchProgram(id).then(p => { setProgram(p); setPageLoading(false); }).catch(() => setPageLoading(false));
  }, [id]);

  if (pageLoading) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!program) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Formation non trouvée</p>
      </div>
    );
  }

  const handleEnrollKit = async (kit) => {
    // Show email modal first
    setPendingPurchase({ type: 'kit', kit });
    setShowEmailModal(true);
  };

  const handleEnroll = async () => {
    // Show email modal first
    setPendingPurchase({ type: 'formula', formula: selectedFormula });
    setShowEmailModal(true);
  };

  const processPayment = async () => {
    if (!customerEmail || !customerEmail.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);
    setShowEmailModal(false);

    try {
      const origin = window.location.origin;
      let requestData = {
        programId: program.id,
        origin: origin,
        email: customerEmail
      };

      if (pendingPurchase.type === 'kit') {
        const kit = pendingPurchase.kit;
        requestData = {
          ...requestData,
          formula: 'kit',
          kitId: kit.id,
          kitTitle: kit.title,
          price: kit.price,
          unlimited: false,
          urgentVAE: false
        };
      } else {
        requestData = {
          ...requestData,
          formula: pendingPurchase.formula,
          unlimited: pendingPurchase.formula === 'gold',
          urgentVAE: false
        };
      }

      const response = await axios.post(`${BACKEND_URL}/api/payments/checkout`, requestData);

      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Erreur lors de la création de la session de paiement');
        setLoading(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
      setLoading(false);
    }
  };

  // Check if program has 3-tier pricing (DEAP)
  const hasThreeTierPricing = program.hasThreeTierPricing;

  const getSelectedPrice = () => {
    if (hasThreeTierPricing) {
      switch (selectedFormula) {
        case 'bronze': return program.priceBronze;
        case 'silver': return program.priceSilver;
        case 'gold': return program.priceGold;
        default: return program.priceSilver;
      }
    } else {
      return selectedFormula === 'unlimited' ? program.priceUnlimited : program.price;
    }
  };

  // DEAP 3-tier pricing
  if (hasThreeTierPricing) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--bg-page)' }}>
        {/* Hero */}
        <section style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '3rem 1.2rem 2rem',
          textAlign: 'center'
        }}>
          <div className="container" style={{ maxWidth: '900px' }}>
            <h1 className="heading-hero" style={{ marginBottom: '0.75rem', color: 'white' }}>
              Choisissez votre formule
            </h1>
            <p className="body-large" style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '1.5rem' }}>
              {program.title} - {program.fullName}
            </p>
            
            {/* Tabs */}
            <div style={{ 
              display: 'inline-flex', 
              background: 'rgba(255,255,255,0.2)', 
              borderRadius: '0.75rem',
              padding: '0.25rem'
            }}>
              <button
                onClick={() => setPricingView('formules')}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  background: pricingView === 'formules' ? 'white' : 'transparent',
                  color: pricingView === 'formules' ? '#667eea' : 'white',
                  transition: 'all 0.2s'
                }}
                data-testid="tab-formules"
              >
                Formules Complètes
              </button>
              <button
                onClick={() => setPricingView('kits')}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  background: pricingView === 'kits' ? 'white' : 'transparent',
                  color: pricingView === 'kits' ? '#667eea' : 'white',
                  transition: 'all 0.2s'
                }}
                data-testid="tab-kits"
              >
                Kits par Bloc
              </button>
            </div>
          </div>
        </section>

        {/* FORMULES COMPLÈTES VIEW */}
        {pricingView === 'formules' && (
          <>
            {/* Pricing Cards */}
            <div className="container" style={{ 
              maxWidth: '1200px',
              padding: '3rem 1.2rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
              gap: '1.5rem'
            }}>
          
          {/* BRONZE */}
          <div 
            className={`card hover-lift`}
            style={{ 
              padding: '2rem',
              cursor: 'pointer',
              border: selectedFormula === 'bronze' ? '3px solid #CD7F32' : '1px solid #e5e7eb',
              position: 'relative',
              background: selectedFormula === 'bronze' ? 'linear-gradient(180deg, #FDF5E6 0%, #FFFFFF 100%)' : 'white'
            }}
            onClick={() => setSelectedFormula('bronze')}
            data-testid="formula-bronze"
          >
            {selectedFormula === 'bronze' && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#CD7F32',
                color: 'white',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Check size={20} />
              </div>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #CD7F32 0%, #B87333 100%)',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Award size={28} color="white" />
              </div>
              <div>
                <span style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  color: '#CD7F32'
                }}>
                  BRONZE
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.25rem', color: '#333' }}>
                {program.priceBronze.toFixed(0)}€
              </div>
              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                Accès 1 an · Paiement unique ou en 3x
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                Cette formule comprend :
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {(program.featuresBronze || []).map((feature, idx) => (
                  <li key={idx} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                    <Check size={20} style={{ color: '#CD7F32', flexShrink: 0, marginTop: '0.1rem' }} />
                    <span className="body-small">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="caption" style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              {program.taglineBronze || 'Idéal pour les candidats autonomes'}
            </p>
            <p className="caption" style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              ou 3x {Math.round(program.priceBronze / 3)}€ sans frais
            </p>
          </div>

          {/* SILVER */}
          <div 
            className={`card hover-lift`}
            style={{ 
              padding: '2rem',
              cursor: 'pointer',
              border: selectedFormula === 'silver' ? '3px solid #C0C0C0' : '1px solid #e5e7eb',
              position: 'relative',
              background: selectedFormula === 'silver' ? 'linear-gradient(180deg, #F8F8F8 0%, #FFFFFF 100%)' : 'white',
              transform: 'scale(1.02)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
            }}
            onClick={() => setSelectedFormula('silver')}
            data-testid="formula-silver"
          >
            {/* Recommended badge */}
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '0.35rem 1rem',
              borderRadius: '2rem',
              fontSize: '0.75rem',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}>
              RECOMMANDÉ
            </div>

            {selectedFormula === 'silver' && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#C0C0C0',
                color: 'white',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Check size={20} />
              </div>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem',
              marginTop: '0.5rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Star size={28} color="white" />
              </div>
              <div>
                <span style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  color: '#808080'
                }}>
                  ARGENT
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.25rem', color: '#333' }}>
                {program.priceSilver.toFixed(0)}€
              </div>
              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                Accès 1 an · Paiement unique ou en 3x
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                Cette formule comprend :
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {(program.featuresSilver || []).map((feature, idx) => (
                  <li key={idx} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                    <Check size={20} style={{ color: '#808080', flexShrink: 0, marginTop: '0.1rem' }} />
                    <span className="body-small">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="caption" style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              {program.taglineSilver || 'Un accompagnement complet pour réussir votre VAE'}
            </p>
            <p className="caption" style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              ou 3x {Math.round(program.priceSilver / 3)}€ sans frais
            </p>
          </div>

          {/* GOLD */}
          <div 
            className={`card hover-lift`}
            style={{ 
              padding: '2rem',
              cursor: 'pointer',
              border: selectedFormula === 'gold' ? '3px solid #FFD700' : '1px solid #e5e7eb',
              position: 'relative',
              background: selectedFormula === 'gold' ? 'linear-gradient(180deg, #FFFEF0 0%, #FFFFFF 100%)' : 'white'
            }}
            onClick={() => setSelectedFormula('gold')}
            data-testid="formula-gold"
          >
            {selectedFormula === 'gold' && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#FFD700',
                color: '#333',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Check size={20} />
              </div>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Crown size={28} color="white" />
              </div>
              <div>
                <span style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  color: '#DAA520'
                }}>
                  OR
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.25rem', color: '#333' }}>
                {program.priceGold.toFixed(0)}€
              </div>
              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                Accès 1 an · Paiement unique ou en 3x
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                Cette formule comprend :
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {(program.featuresGold || []).map((feature, idx) => (
                  <li key={idx} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                    <Check size={20} style={{ color: '#DAA520', flexShrink: 0, marginTop: '0.1rem' }} />
                    <span className="body-small">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="caption" style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              {program.taglineGold || "L'accompagnement premium pour maximiser vos chances de réussite"}
            </p>
            <p className="caption" style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              ou 3x {Math.round(program.priceGold / 3)}€ sans frais
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="container" style={{ maxWidth: '600px', padding: '2rem 1.2rem 4rem', textAlign: 'center' }}>
          <button
            onClick={handleEnroll}
            disabled={loading}
            className="btn-primary"
            style={{
              width: '100%',
              justifyContent: 'center',
              fontSize: '1.1rem',
              padding: '1.25rem 2rem',
              opacity: loading ? 0.7 : 1,
              background: selectedFormula === 'bronze' ? 'linear-gradient(135deg, #CD7F32 0%, #B87333 100%)' :
                         selectedFormula === 'silver' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
                         'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: selectedFormula === 'gold' ? '#333' : 'white'
            }}
            data-testid="enroll-button"
          >
            {loading ? 'Chargement...' : `Choisir ${selectedFormula === 'silver' ? 'ARGENT' : selectedFormula === 'gold' ? 'OR' : selectedFormula.toUpperCase()} - ${getSelectedPrice().toFixed(0)}€`}
          </button>
          <p className="caption" style={{ marginTop: '1rem', color: 'var(--text-muted)' }}>
            Paiement sécurisé par Stripe — Paiement en 3 fois disponible via Klarna
          </p>
        </div>

        {/* Comparison Table */}
        <div className="container" style={{ maxWidth: '900px', padding: '2rem 1.2rem 4rem' }}>
          <h2 className="heading-2" style={{ marginBottom: '2rem', textAlign: 'center' }}>
            Comparatif des formules
          </h2>
          <div className="card" style={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch', padding: '0.5rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>Fonctionnalité</th>
                  <th style={{ padding: '0.75rem 0.25rem', textAlign: 'center', color: '#CD7F32' }}>Bronze</th>
                  <th style={{ padding: '0.75rem 0.25rem', textAlign: 'center', color: '#808080' }}>Argent</th>
                  <th style={{ padding: '0.75rem 0.25rem', textAlign: 'center', color: '#DAA520' }}>Or</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem 0.5rem' }}>Formation en ligne ({program.id === 'deeje' ? '4 DC - 8 blocs' : program.id === 'bac-assp' ? '4 blocs + PSE + Outils' : '11 activités'})</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><Check size={18} color="#22c55e" /></td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><Check size={18} color="#22c55e" /></td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><Check size={18} color="#22c55e" /></td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem 0.5rem' }}>Livret 2 annoté</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><Check size={18} color="#22c55e" /></td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><Check size={18} color="#22c55e" /></td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><Check size={18} color="#22c55e" /></td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem 0.5rem' }}>Corrections par {program.id === 'deeje' ? 'bloc' : program.id === 'bac-assp' ? 'bloc' : 'activité'}</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><X size={18} color="#ef4444" /></td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center', fontSize: '0.8rem' }}>1 par {program.id === 'deeje' ? 'bloc' : program.id === 'bac-assp' ? 'bloc' : 'activité'}</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center', fontWeight: 600, color: '#22c55e', fontSize: '0.8rem' }}>Illimitées</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem 0.5rem' }}>Appels d'accompagnement</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><X size={18} color="#ef4444" /></td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center', fontSize: '0.8rem' }}>1 heure</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center', fontWeight: 600, color: '#22c55e', fontSize: '0.8rem' }}>À chaque correction</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem 0.5rem' }}>Oraux blancs</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><X size={18} color="#ef4444" /></td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center' }}><X size={18} color="#ef4444" /></td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center', fontWeight: 600, color: '#22c55e', fontSize: '0.8rem' }}>3 séances</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600 }}>Prix</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center', fontWeight: 600, color: '#CD7F32' }}>{program.priceBronze}€</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center', fontWeight: 600, color: '#808080' }}>{program.priceSilver}€</td>
                  <td style={{ padding: '0.75rem 0.25rem', textAlign: 'center', fontWeight: 600, color: '#DAA520' }}>{program.priceGold}€</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
          </>
        )}

        {/* KITS PAR BLOC VIEW */}
        {pricingView === 'kits' && program.hasKitPricing && program.kits && (
          <div className="container" style={{ maxWidth: '1100px', padding: '2rem 1.2rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '0.5rem' }}>
                Achetez bloc par bloc
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
                Idéal si vous souhaitez avancer à votre rythme ou vous concentrer sur un bloc spécifique
              </p>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
              gap: '1.25rem',
              marginBottom: '2rem'
            }}>
              {program.kits.map((kit) => (
                <div
                  key={kit.id}
                  className="card hover-lift"
                  style={{
                    cursor: 'pointer',
                    border: selectedKit === kit.id ? `3px solid ${kit.color}` : '1px solid #e5e7eb',
                    padding: '1.5rem',
                    position: 'relative',
                    background: selectedKit === kit.id ? `${kit.color}10` : 'white',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setSelectedKit(selectedKit === kit.id ? null : kit.id)}
                  data-testid={`kit-${kit.id}`}
                >
                  {selectedKit === kit.id && (
                    <div style={{
                      position: 'absolute',
                      top: '0.75rem',
                      right: '0.75rem',
                      background: kit.color,
                      color: 'white',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Check size={16} />
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      background: kit.color,
                      borderRadius: '0.5rem',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>
                      {kit.id.match(/\d+/)?.[0] || kit.id.split('-').pop()}
                    </div>
                    <div>
                      <h4 style={{ fontWeight: '600', color: kit.color, fontSize: '1rem', marginBottom: '0.1rem' }}>
                        {kit.title}
                      </h4>
                    </div>
                  </div>

                  <p style={{ fontSize: '0.85rem', fontWeight: '500', color: '#333', marginBottom: '0.5rem' }}>
                    {kit.subtitle}
                  </p>
                  <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.75rem' }}>
                    {kit.description}
                  </p>

                  <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '1rem' }}>
                    {kit.activities ? kit.activities.join(' • ') : kit.blocs ? kit.blocs.join(' • ') : ''}
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <div>
                      <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: kit.color }}>
                        {kit.price}€
                      </span>
                    </div>
                    <Package size={20} style={{ color: kit.color }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Kit Purchase Button */}
            {selectedKit && (
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => {
                    const kit = program.kits.find(k => k.id === selectedKit);
                    if (kit) handleEnrollKit(kit);
                  }}
                  disabled={loading}
                  className="btn-primary"
                  style={{
                    padding: '1rem 2rem',
                    fontSize: '1rem',
                    background: program.kits.find(k => k.id === selectedKit)?.color || '#667eea',
                    opacity: loading ? 0.7 : 1
                  }}
                  data-testid="buy-kit-button"
                >
                  {loading ? 'Chargement...' : `Acheter ${program.kits.find(k => k.id === selectedKit)?.title} - ${program.kits.find(k => k.id === selectedKit)?.price}€`}
                </button>
                <p className="caption" style={{ marginTop: '0.75rem', color: 'var(--text-muted)' }}>
                  {(program.kits.find(k => k.id === selectedKit)?.price || 0) >= 100
                    ? 'Paiement sécurisé par Stripe — Paiement en 3 fois disponible via Klarna'
                    : 'Paiement sécurisé par Stripe'}
                </p>
              </div>
            )}

            {/* Kit Info Card */}
            <div className="card" style={{ 
              marginTop: '2rem', 
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              border: '1px solid #bae6fd'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: '#0369a1' }}>
                💡 Bon à savoir sur les Kits
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  <Check size={18} style={{ color: '#10B981', flexShrink: 0 }} />
                  <span>Chaque kit inclut les cours en ligne ET la partie du Livret 2 annotée</span>
                </li>
                <li style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  <Check size={18} style={{ color: '#10B981', flexShrink: 0 }} />
                  <span>Vous pouvez acheter plusieurs kits et les cumuler</span>
                </li>
                <li style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  <Check size={18} style={{ color: '#10B981', flexShrink: 0 }} />
                  <span>Passez à une formule complète (Bronze/Argent/Or) à tout moment avec déduction du montant déjà payé</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* FAQ */}
        <div className="container" style={{ maxWidth: '800px', padding: '2rem 1.2rem 4rem' }}>
          <h2 className="heading-2" style={{ marginBottom: '2rem', textAlign: 'center' }}>
            Questions fréquentes
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="card">
              <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                Quelle formule choisir ?
              </h3>
              <p className="body-small">
                <strong>Bronze</strong> : Vous êtes autonome et avez juste besoin du contenu de formation et d'un modèle.<br/>
                <strong>Argent</strong> : Vous souhaitez un accompagnement structuré avec des retours sur votre travail.<br/>
                <strong>Or</strong> : Vous voulez un accompagnement complet avec préparation à l'oral.<br/>
                <strong>Kit Bloc</strong> : Vous souhaitez avancer bloc par bloc selon votre budget.
              </p>
            </div>

            <div className="card">
              <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                Puis-je changer de formule en cours de route ?
              </h3>
              <p className="body-small">
                Oui, vous pouvez passer à une formule supérieure en payant la différence. 
                Si vous avez acheté des Kits Bloc, le montant sera déduit de la formule complète.
                Contactez-nous à apdr.vae@gmail.com pour effectuer ce changement.
              </p>
            </div>

            <div className="card">
              <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                Comment se passent les oraux blancs ?
              </h3>
              <p className="body-small">
                Avec la formule Or, vous bénéficiez de 3 séances d'oral blanc en visioconférence. 
                Ces sessions simulent les conditions réelles du jury pour vous préparer au mieux.
              </p>
            </div>
          </div>
        </div>

        {/* Email Modal */}
        {showEmailModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowEmailModal(false)}
          >
            <div 
              className="card"
              style={{
                maxWidth: '450px',
                width: '100%',
                padding: '2rem',
                background: 'white',
                borderRadius: '1rem'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Mail size={28} color="white" />
                </div>
                <h2 className="heading-2" style={{ marginBottom: '0.5rem' }}>
                  Où envoyer vos documents ?
                </h2>
                <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                  Après votre paiement, vous recevrez automatiquement votre Livret 2 annoté par email.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Votre adresse email
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="exemple@email.com"
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  data-testid="email-input"
                />
              </div>

              <button
                onClick={processPayment}
                disabled={loading || !customerEmail}
                className="btn-primary"
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1rem',
                  justifyContent: 'center',
                  opacity: (loading || !customerEmail) ? 0.6 : 1
                }}
                data-testid="confirm-payment-btn"
              >
                {loading ? 'Chargement...' : 'Continuer vers le paiement'}
              </button>

              <p className="caption" style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-muted)' }}>
                🔒 Paiement sécurisé par Stripe
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default 2-tier pricing for other programs
  const standardPrice = program.price;
  const unlimitedPrice = program.priceUnlimited;

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--bg-page)' }}>
      {/* Hero */}
      <section style={{ 
        background: 'var(--gradient-hero-warm)',
        padding: '4rem 1.2rem 3rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            Choisissez votre formule
          </h1>
          <p className="body-large">
            {program.title} - {program.fullName}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <div className="container" style={{ 
        maxWidth: '1100px',
        padding: '3rem 1.2rem',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '2rem'
      }}>
        {/* Standard Formula */}
        <div 
          className={`card hover-lift ${selectedFormula === 'standard' ? 'accent-purple' : ''}`}
          style={{ 
            padding: '2rem',
            cursor: 'pointer',
            border: selectedFormula === 'standard' ? '3px solid var(--text-primary)' : 'none',
            position: 'relative'
          }}
          onClick={() => setSelectedFormula('standard')}
        >
          {selectedFormula === 'standard' && (
            <div style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'var(--text-primary)',
              color: 'white',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Check size={20} />
            </div>
          )}

          <div style={{
            background: 'var(--accent-blue-200)',
            padding: '0.5rem 1rem',
            borderRadius: '2rem',
            width: 'fit-content',
            marginBottom: '1.5rem'
          }}>
            <span className="caption font-mono" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
              Formule Standard
            </span>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <div className="heading-hero" style={{ marginBottom: '0.5rem' }}>
              {standardPrice.toFixed(2)}€
            </div>
            <p className="body-small" style={{ color: 'var(--text-muted)' }}>
              Paiement unique
            </p>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
              Cette formule comprend :
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                <Check size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
                <span className="body-small">{program.sessions} séances d'accompagnement</span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                <Check size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
                <span className="body-small">Accès complet à la plateforme de formation</span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                <Check size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
                <span className="body-small">Support par email</span>
              </li>
            </ul>
          </div>

          <p className="caption" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            Idéal si vous avez un bon niveau d'autonomie
          </p>
        </div>

        {/* Unlimited Formula */}
        <div 
          className={`card hover-lift ${selectedFormula === 'unlimited' ? 'accent-green' : ''}`}
          style={{ 
            padding: '2rem',
            cursor: 'pointer',
            border: selectedFormula === 'unlimited' ? '3px solid var(--text-primary)' : 'none',
            position: 'relative'
          }}
          onClick={() => setSelectedFormula('unlimited')}
        >
          {selectedFormula === 'unlimited' && (
            <div style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'var(--text-primary)',
              color: 'white',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Check size={20} />
            </div>
          )}

          <div style={{
            background: 'var(--accent-orange-200)',
            padding: '0.5rem 1rem',
            borderRadius: '2rem',
            width: 'fit-content',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span className="caption font-mono" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
              Formule Illimitée
            </span>
            <span style={{
              background: '#4CAF50',
              color: 'white',
              padding: '0.125rem 0.5rem',
              borderRadius: '1rem',
              fontSize: '0.65rem',
              fontWeight: 600
            }}>
              RECOMMANDÉ
            </span>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <div className="heading-hero" style={{ marginBottom: '0.5rem' }}>
              {unlimitedPrice.toFixed(2)}€
            </div>
            <p className="body-small" style={{ color: 'var(--text-muted)' }}>
              Paiement unique
            </p>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
              Cette formule comprend :
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                <Check size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
                <span className="body-small" style={{ fontWeight: 600 }}>
                  Séances ILLIMITÉES pendant {program.duration}
                </span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                <Check size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
                <span className="body-small">Accès complet à la plateforme de formation</span>
              </li>
              <li style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                <Check size={20} style={{ color: 'green', flexShrink: 0, marginTop: '0.2rem' }} />
                <span className="body-small">Support prioritaire par email</span>
              </li>
            </ul>
          </div>

          <p className="caption" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            Idéal pour un accompagnement complet
          </p>
        </div>
      </div>

      {/* CTA Button */}
      <div className="container" style={{ maxWidth: '600px', padding: '2rem 1.2rem 4rem', textAlign: 'center' }}>
        <button
          onClick={handleEnroll}
          disabled={loading}
          className="btn-primary"
          style={{
            width: '100%',
            justifyContent: 'center',
            fontSize: '1rem',
            padding: '1rem 2rem',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? 'Chargement...' : `Procéder au paiement - ${selectedFormula === 'standard' ? standardPrice.toFixed(2) : unlimitedPrice.toFixed(2)}€`}
        </button>
        <p className="caption" style={{ marginTop: '1rem' }}>
          Paiement sécurisé par Stripe
        </p>
      </div>
    </div>
  );
};

export default ProgramPricingPage;
